import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CgMore } from 'react-icons/cg';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useModal } from '@/hooks';
import { LogModal } from '@/components/modals';
import { Flex, LogTable, LogTableColumn } from '@/components/common';
import { envConfig } from '@/configs';
import { useSelfContext } from '@/context';
import { activityLogDetailsWithoutPriceFields } from '@/services/mappers/activity';
import { fetchActivityLogs } from '@/services/activity.service';

const PAGE_SIZE = 10;

export interface ActivityReportDesktopProps {
  fromDate: string;
  toDate: string;
  actionTypes: string[];
}

export function ActivityReportDesktop({
  fromDate,
  toDate,
  actionTypes,
}: ActivityReportDesktopProps) {

  const { findVariantById, loading: selfLoading } = useSelfContext();
  const [t] = useTranslation(['common', 'report']);

  const filterKey = useMemo(
    () => `${fromDate}|${toDate}|${actionTypes.join('\0')}`,
    [fromDate, toDate, actionTypes]
  );
  const [pagesByFilter, setPagesByFilter] = useState<Record<string, number>>({});
  const page = pagesByFilter[filterKey] ?? 0;

  const setPage = useCallback((newPage: number) => {
    setPagesByFilter((prev) => ({ ...prev, [filterKey]: newPage }));
  }, [filterKey]);

  const logsQuery = useQuery({
    retry: 0,
    enabled: !selfLoading,
    placeholderData: keepPreviousData,
    queryKey: ['/rest/users/activity-logs', page, fromDate, toDate, actionTypes],
    queryFn: () => fetchActivityLogs({
      actionTypes,
      page,
      limit: PAGE_SIZE,
      fromDate,
      toDate,
      t,
      findVariantById,
    }),
  });

  const TABLE_HEADERS = [
    { width: 200, key: 'name', title: t('report:labels.action-type') },
    {
      width: 100,
      key: ['details', 'foodNames', 'value'],
      render: (text) => <div className="w-full flex">
        <span className="truncate w-[200px]">
          {text}
        </span>
      </div>,
      title: t('report:labels.entity-time'),
    },
    {
      key: 'fullDate',
      title: t('common:labels.date'),
      align: 'center',
      render: (text) => <div style={{ direction: 'ltr', textAlign: 'left' }}>{text}</div>
    },
    {
      key: 'creditType',
      title: t('report:activity-report-activity-fields.creditType'),
      align: 'center',
    },
    envConfig.ui.removePrices ? null : { key: 'price', title: t('report:activity-report-activity-fields.amount'), align: 'center' },
    { key: 'serveDate', align: 'center', title: t('report:activity-report-activity-fields.serveDate') },
    envConfig.ui.onlySubsidy || envConfig.ui.removePrices ? null : {
      key: ['details', 'remainCredit', 'value'],
      title: t('report:activity-report-activity-fields.remainCredit'),
      align: 'center'
    } as any,
    { key: ['action'], title: ' ', align: 'center' }
  ].filter(x => x != null) as LogTableColumn[];

  const modal = useModal();
  const items = logsQuery.data?.items ?? [];
  const total = logsQuery.data?.total;

  const rows = items.map(item => ({
    ...item,
    action: (
      <Flex
        inlineFlex
        align="center"
        justify="center"
        onClick={() => modal.show(activityLogDetailsWithoutPriceFields(item.details))}
        className="p-1 rounded-lg cursor-pointer hover:bg-gray-200">
        <CgMore style={{ fontSize: '1.3rem' }} />
      </Flex>
    )
  }));

  return (
    <>
      <LogModal {...modal} />
      <LogTable
        size="small"
        rows={rows}
        className="no-last-th-border"
        columns={TABLE_HEADERS}
        loading={logsQuery.isFetching}
        pagination={{
          total: total,
          current: page + 1,
          pageSize: PAGE_SIZE,
          onChange: (nextPage) => setPage(nextPage - 1),
          showSizeChanger: false,
        }}
      />
    </>
  );
}

export default ActivityReportDesktop;
